/**
 * User-related API service functions
 */
import apiClient from './apiClient';
import { API_ENDPOINTS } from '../constants/api';

export const userService = {
  /**
   * Get user profile
   * @returns {Promise} User profile data
   */
  getProfile: async () => {
    return apiClient.get(API_ENDPOINTS.USERS.PROFILE);
  },

  /**
   * Update user profile
   * @param {Object} profileData - Updated profile information
   * @returns {Promise} Updated profile data
   */
  updateProfile: async (profileData) => {
    return apiClient.put(API_ENDPOINTS.USERS.UPDATE_PROFILE, profileData);
  },

  /**
   * Change user password
   * @param {Object} passwordData - Password change data
   * @returns {Promise} Success confirmation
   */
  changePassword: async (passwordData) => {
    return apiClient.post(API_ENDPOINTS.USERS.CHANGE_PASSWORD, passwordData);
  },

  /**
   * Upload user avatar
   * @param {File} avatarFile - Avatar image file
   * @returns {Promise} Upload result with avatar URL
   */
  uploadAvatar: async (avatarFile) => {
    const formData = new FormData();
    formData.append('avatar', avatarFile);
    return apiClient.upload(API_ENDPOINTS.USERS.UPLOAD_AVATAR, formData);
  },

  /**
   * Get user preferences
   * @returns {Promise} User preferences
   */
  getPreferences: async () => {
    return apiClient.get(API_ENDPOINTS.USERS.PREFERENCES);
  },

  /**
   * Update user preferences
   * @param {Object} preferences - Updated preferences
   * @returns {Promise} Updated preferences
   */
  updatePreferences: async (preferences) => {
    return apiClient.put(API_ENDPOINTS.USERS.PREFERENCES, preferences);
  },

  /**
   * Get user's saved passengers
   * @returns {Promise} List of saved passengers
   */
  getSavedPassengers: async () => {
    return apiClient.get(`${API_ENDPOINTS.USERS.PROFILE}/passengers`);
  },

  /**
   * Add a saved passenger
   * @param {Object} passengerData - Passenger information
   * @returns {Promise} Added passenger data
   */
  addSavedPassenger: async (passengerData) => {
    return apiClient.post(`${API_ENDPOINTS.USERS.PROFILE}/passengers`, passengerData);
  },

  /**
   * Update a saved passenger
   * @param {string} passengerId - Passenger ID
   * @param {Object} passengerData - Updated passenger information
   * @returns {Promise} Updated passenger data
   */
  updateSavedPassenger: async (passengerId, passengerData) => {
    return apiClient.put(`${API_ENDPOINTS.USERS.PROFILE}/passengers/${passengerId}`, passengerData);
  },

  /**
   * Delete a saved passenger
   * @param {string} passengerId - Passenger ID
   * @returns {Promise} Deletion confirmation
   */
  deleteSavedPassenger: async (passengerId) => {
    return apiClient.delete(`${API_ENDPOINTS.USERS.PROFILE}/passengers/${passengerId}`);
  },

  /**
   * Get user's payment methods
   * @returns {Promise} List of payment methods
   */
  getPaymentMethods: async () => {
    return apiClient.get(API_ENDPOINTS.PAYMENTS.METHODS);
  },

  /**
   * Add a payment method
   * @param {Object} paymentMethodData - Payment method information
   * @returns {Promise} Added payment method
   */
  addPaymentMethod: async (paymentMethodData) => {
    return apiClient.post(API_ENDPOINTS.PAYMENTS.METHODS, paymentMethodData);
  },

  /**
   * Delete a payment method
   * @param {string} paymentMethodId - Payment method ID
   * @returns {Promise} Deletion confirmation
   */
  deletePaymentMethod: async (paymentMethodId) => {
    return apiClient.delete(`${API_ENDPOINTS.PAYMENTS.METHODS}/${paymentMethodId}`);
  },

  /**
   * Get user's saved cards
   * @returns {Promise} List of saved cards
   */
  getSavedCards: async () => {
    return apiClient.get('/user/saved-cards');
  },

  /**
   * Delete a saved card
   * @param {string} cardId - Card ID
   * @returns {Promise} Deletion confirmation
   */
  deleteCard: async (cardId) => {
    return apiClient.post('/user/delete-card', { cardId });
  }
};

export default userService;